import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Download, Sparkles } from "lucide-react";
import { ArchetypeCard } from "../components/glimpse/ArchetypeCard";
import { ScoreGauge } from "../components/glimpse/ScoreGauge";
import { StrengthSignal } from "../components/glimpse/StrengthSignal";
import { OceanBars } from "../components/glimpse/OceanBars";
import { PlanPreview } from "../components/glimpse/PlanPreview";
import { ClarityStatement } from "../components/glimpse/ClarityStatement";
import { IntentCTA } from "../components/glimpse/IntentCTA";
import { ShareEarn } from "../components/glimpse/ShareEarn";
import { Logo } from "../components/shared/Logo";
import { Footer } from "../components/shared/Footer";
import { Button } from "../components/shared/Button";
import { getReport } from "../lib/api";

/**
 * Persistent, re-visitable snapshot view.
 *
 * URL: /snapshot/:response_id?code=KLR.....&fresh=true?
 * Renders the same Glimpse UI from the server's /api/report/:id payload, so
 * users can come back days later via /returning or a saved bookmark.
 */
export default function Snapshot() {
    const navigate = useNavigate();
    const { response_id: responseId } = useParams();
    const [search] = useSearchParams();
    const code = (search.get("code") || "").trim().toUpperCase();
    const fresh = search.get("fresh") === "true";

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        let alive = true;
        if (!responseId || !code) {
            setError("missing");
            setLoading(false);
            return undefined;
        }
        (async () => {
            const res = await getReport({ responseId, code });
            if (!alive) return;
            if (res.ok) {
                setData(res.data);
            } else if (res.forbidden || res.notFound) {
                setError("not_found");
            } else {
                setError("server");
            }
            setLoading(false);
        })();
        return () => {
            alive = false;
        };
    }, [responseId, code]);

    if (loading) {
        return (
            <div className="min-h-screen grid place-items-center klareet-grain klareet-glow">
                <div className="text-center">
                    <div className="inline-block w-8 h-8 rounded-full border-2 border-klareet-bor border-t-klareet-T animate-spin" />
                    <div className="mt-4 text-klareet-MUT font-mono text-sm tracking-widest uppercase">
                        Loading your snapshot…
                    </div>
                </div>
            </div>
        );
    }

    if (error || !data) {
        return (
            <div className="min-h-screen grid place-items-center px-5 klareet-grain klareet-glow">
                <div
                    className="max-w-md w-full text-center rounded-3xl border border-klareet-bor bg-klareet-card p-8"
                    data-testid="snapshot-error"
                >
                    <div className="text-klareet-RED text-2xl font-semibold tracking-tight">
                        Snapshot unavailable
                    </div>
                    <p className="mt-3 text-klareet-MUT leading-relaxed">
                        We couldn't find that snapshot. The link may be
                        incomplete, or this snapshot has been removed. Try
                        searching by email instead.
                    </p>
                    <div className="mt-6 flex justify-center gap-3 flex-wrap">
                        <Button
                            size="md"
                            onClick={() => navigate("/returning")}
                            data-testid="snapshot-error-returning-btn"
                        >
                            Find by email
                        </Button>
                        <Button
                            size="md"
                            variant="outline"
                            onClick={() => navigate("/")}
                        >
                            Home
                        </Button>
                    </div>
                </div>
            </div>
        );
    }

    const reportHref = `/report/${responseId}?code=${code}`;

    return (
        <div className="relative min-h-screen klareet-grain klareet-glow">
            <header className="relative z-10 px-5 md:px-12 pt-6 pb-2 flex items-center justify-between">
                <Link to="/">
                    <Logo className="h-14 md:h-16 w-auto" />
                </Link>
                <div className="flex items-center gap-3">
                    <Button
                        size="sm"
                        variant="outline"
                        onClick={() =>
                            window.open(reportHref, "_blank", "noopener")
                        }
                        data-testid="download-snapshot-btn"
                    >
                        <Download size={16} />
                        Download snapshot
                    </Button>
                    <div className="hidden md:block text-[11px] uppercase tracking-[0.22em] font-mono text-klareet-T2">
                        Your snapshot · glimpse
                    </div>
                </div>
            </header>

            <main className="relative z-10 px-5 md:px-12 pb-20 max-w-4xl mx-auto space-y-8">
                {fresh ? (
                    <motion.div
                        initial={{ opacity: 0, y: -8 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.45 }}
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-klareet-T/40 bg-klareet-T/10 text-sm text-klareet-T2"
                        data-testid="snapshot-fresh-banner"
                    >
                        <Sparkles size={14} />
                        Just submitted. Here's your glimpse.
                    </motion.div>
                ) : null}

                <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] items-center gap-8">
                    <ArchetypeCard archetype={data.archetype} />
                    <div className="justify-self-center">
                        <ScoreGauge score={data.score} />
                    </div>
                </div>

                <StrengthSignal strength={data.strength} />
                <OceanBars baseScore={data.score} />
                <PlanPreview weekOneAction={data.weekOneAction} />
                <ClarityStatement line={data.clarityStatement} />
                <IntentCTA waitlist={data.waitlist ?? "maybe"} />

                <ShareEarn
                    shareCode={data.share_code}
                    responseId={data.response_id}
                    initialEmailOnFile={Boolean(data.email)}
                    variant="glimpse"
                />

                <div className="pt-6 text-center text-sm text-klareet-DIM">
                    This is the glimpse. The full snapshot includes a live
                    conversation, complete psychology profile, and a 90-day
                    execution plan. Released at launch.
                </div>
            </main>

            <Footer />
        </div>
    );
}
